import React, { useEffect, useState, useRef } from "react";
import { Icon } from "@iconify/react";
import { Button } from "../../../components/ui/button";
import { Card, CardHeader, CardContent } from "../../../components/ui/card";
import { PrintReceipt } from "../../../components/print";
import Common from "../../../helper/common";
import {
  BookingStatusEnum,
  PaymentStatusEnum,
  AmenityTypeEnum,
  DateminFormat,
  ViewDateTimeFormat,
  respEnum,
} from "../../../constants/appConstant";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import moment from "moment";
import Swal from "sweetalert2";

const AmenityBookingDetails = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get("id");
  const shouldPrint = searchParams.get("print") === "true";
  const [booking, setBooking] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const printRef = useRef();

  useEffect(() => {
    if (bookingId) {
      loadBookingDetails();
    }
  }, [bookingId]);

  useEffect(() => {
    if (shouldPrint && booking && printRef.current) {
      // Auto-print when the component loads with print=true
      setTimeout(() => {
        try {
          if (printRef.current && printRef.current.print) {
            printRef.current.print();
          }
        } catch (error) {
          console.error("Auto-print failed:", error);
          toast.error("Auto-print failed. Please try printing manually.");
        }
      }, 2000); // Increased timeout to 2 seconds
    }
  }, [shouldPrint, booking]);

  const loadBookingDetails = async () => {
    try {
      setIsLoading(true);
      const response = await Common.ApiService.getInstance().request(
        `GetAmenityBookingDetails?id=${bookingId}`,
        {},
        "Get"
      );

      if (response?.status === respEnum.Success && response?.data) {
        setBooking(response.data);
      } else {
        toast.error("Booking not found");
        navigate("/app/amenitybookings");
      }
    } catch (error) {
      console.error("Error loading booking details:", error);
      toast.error("Error loading booking details");
      navigate("/app/amenitybookings");
    } finally {
      setIsLoading(false);
    }
  };

  // Prepare amenity booking data for printing
  const preparePrintData = () => {
    if (!booking) return {};

    const commonData = {
      clubPhone: booking?.club?.phone,
      clubLogo: booking?.club?.logo,
      "Reference Number": booking?.refno || "N/A",
      "Booking Type": getTypeLabel(booking?.type),
      Amenity:
        booking?.clubamenityName + " (" + booking?.amenityName + ")" || "N/A",
      "Player Name": booking?.playerName || "N/A",
      "Number of Persons": booking?.noofpersons || "N/A",
      "Booking Status":
        booking?.bookingstatus === BookingStatusEnum.Booked
          ? "Booked"
          : booking?.bookingstatus === BookingStatusEnum.Cancelled
          ? "Cancelled"
          : booking?.bookingstatus === BookingStatusEnum.Cancelled_By_Club
          ? "Cancelled by Club"
          : booking?.bookingstatus === BookingStatusEnum.Pending
          ? "Pending"
          : "Unknown",
      "Payment Status":
        booking?.paymentstatus === PaymentStatusEnum.Paid
          ? "Paid"
          : booking?.paymentstatus === PaymentStatusEnum.Un_Paid
          ? "Unpaid"
          : booking?.paymentstatus === PaymentStatusEnum.Partial_Paid
          ? "Partial"
          : "Unknown",
      Charges: booking?.charges
        ? `Rs. ${booking.charges.toFixed(2)}`
        : "Rs. 0.00",
      Discount: booking?.discountamount
        ? `Rs. ${booking.discountamount.toFixed(2)}`
        : "Rs. 0.00",
      Total: booking?.total ? `Rs. ${booking.total.toFixed(2)}` : "Rs. 0.00",
      "Received Amount": booking?.receivedamount
        ? `Rs. ${booking.receivedamount.toFixed(2)}`
        : "Rs. 0.00",
      "Remaining Amount": booking?.remainingamount
        ? `Rs. ${booking.remainingamount.toFixed(2)}`
        : "Rs. 0.00",
    };

    if (booking?.type === AmenityTypeEnum.Session_Based) {
      return {
        ...commonData,
        "Booking Date": booking?.bookingdate
          ? moment(booking.bookingdate).format("dddd, DD MMM yyyy")
          : "N/A",
        "Start Time": booking?.startdatetime
          ? moment(booking.startdatetime).format("hh:mm A")
          : "N/A",
        "End Time": booking?.enddatetime
          ? moment(booking.enddatetime).format("hh:mm A")
          : "N/A",
        Duration: booking?.duration ? `${booking.duration}` : "N/A",
      };
    } else if (booking?.type === AmenityTypeEnum.Package_Based) {
      return {
        ...commonData,
        "Valid From": booking?.startdatetime
          ? moment(booking.startdatetime).format("DD MMM yyyy, hh:mm A")
          : "N/A",
        "Valid Till": booking?.enddatetime
          ? moment(booking.enddatetime).format("DD MMM yyyy, hh:mm A")
          : "N/A",
        // "Daily Time":
        //   booking?.starttime && booking?.endtime
        //     ? `${booking.starttime} - ${booking.endtime}`
        //     : "N/A",
        "Selected Days": booking?.selecteddays
          ? getDaysLabel(booking.selecteddays)
          : "N/A",
      };
    }

    return commonData;
  };

  const getBookingStatusLabel = (status) => {
    switch (status) {
      case BookingStatusEnum.Booked:
        return (
          <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
            Booked
          </span>
        );
      case BookingStatusEnum.Cancelled:
        return (
          <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
            Cancelled
          </span>
        );
      case BookingStatusEnum.Cancelled_By_Club:
        return (
          <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
            Cancelled by Club
          </span>
        );
      case BookingStatusEnum.Pending:
        return (
          <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">
            Pending
          </span>
        );
      default:
        return (
          <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-medium">
            Unknown
          </span>
        );
    }
  };

  const getPaymentStatusLabel = (status) => {
    switch (status) {
      case PaymentStatusEnum.Paid:
        return (
          <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
            Paid
          </span>
        );
      case PaymentStatusEnum.Un_Paid:
        return (
          <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
            Unpaid
          </span>
        );
      case PaymentStatusEnum.Partial_Paid:
        return (
          <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">
            Partial
          </span>
        );
      default:
        return (
          <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-medium">
            Unknown
          </span>
        );
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case AmenityTypeEnum.Session_Based:
        return "Session Based";
      case AmenityTypeEnum.Package_Based:
        return "Package Based";
      default:
        return "Unknown";
    }
  };

  const getDaysLabel = (selectedDays) => {
    if (!selectedDays) return "Not specified";

    try {
      const days = JSON.parse(selectedDays);
      const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      return days.map((day) => dayNames[day]).join(", ");
    } catch {
      return "Not specified";
    }
  };

  const handleCancelBooking = async () => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This action can't be reverted! The booking will be cancelled permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, cancel booking!",
      cancelButtonText: "No, keep booking",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        const response = await Common.ApiService.getInstance().request(
          `UpdateAmenityBookingStatus?id=${bookingId}&status=${BookingStatusEnum.Cancelled_By_Club}`,
          {},
          "Post"
        );

        if (response?.status === respEnum.Success) {
          toast.success("Booking cancelled successfully");
          // Reload booking details to show updated status
          await loadBookingDetails();
        } else {
          toast.error(response?.message || "Error cancelling booking");
        }
      } catch (error) {
        console.error("Error cancelling booking:", error);
        toast.error("Error cancelling booking. Please try again.");
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Icon
          icon="eos-icons:loading"
          className="animate-spin text-4xl text-[#0C1B3A]"
        />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="container mx-auto p-3">
        <div className="text-center text-gray-500">
          <Icon
            icon="heroicons:exclamation-triangle"
            className="mx-auto text-6xl mb-4"
          />
          <h2 className="text-xl font-semibold mb-2">Booking Not Found</h2>
          <p>The requested amenity booking could not be found.</p>
          <Button
            onClick={() => navigate("/app/amenitybookings")}
            className="mt-4 bg-[#0C1B3A] hover:bg-[#1a2a4e] text-white"
          >
            Back to Bookings
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-3">
      <div className="flex justify-between items-center mb-4 flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-bold text-[#0C1B3A] flex items-center">
          <Icon icon="heroicons:eye" className="mr-2" />
          Amenity Booking Details
        </h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            // onClick={() => navigate("/app/amenitybookings")}
            onClick={() => navigate(-1)}
            // className="border-[#0C1B3A] text-[#0C1B3A] hover:bg-[#e8eaf0]"
            className=" border-[#0C1B3A] text-[#0C1B3A] bg-white hover:bg-[#0C1B3A] hover:text-white hover:border-[#0C1B3A]"
          >
            <Icon icon="heroicons:arrow-left" className="mr-1" />
            Back to List
          </Button>

          {/* Show cancel button only if booking is not already cancelled */}
          {booking &&
            booking.bookingstatus !== BookingStatusEnum.Cancelled &&
            booking.bookingstatus !== BookingStatusEnum.Cancelled_By_Club && (
              <Button
                variant="outline"
                onClick={handleCancelBooking}
                // className="border-red-500 text-red-600 hover:bg-red-50"
                className=" border-red-500 text-red-600 bg-white hover:bg-red-600 hover:text-white hover:border-red-600"

              >
                <Icon icon="heroicons:x-circle" className="mr-1" />
                Cancel Booking
              </Button>
            )}

          <PrintReceipt
            ref={printRef}
            data={preparePrintData()}
            title={booking?.clubName}
            subtitle="Facility Booking Receipt"
            showPrintButton={true}
            showViewButton={false}
            buttonText="Print"
            buttonIcon="heroicons:printer"
            buttonSize="sm"
            buttonVariant="outline"
            autoPrint={shouldPrint && booking !== null}
          />
        </div>
      </div>

      <div id="printableContent">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Booking Information */}
          <Card className="shadow-sm border-0">
            <CardHeader className="bg-[#0C1B3A] text-white">
              <h3 className="text-lg font-semibold flex items-center">
                <Icon icon="heroicons:information-circle" className="mr-2" />
                Booking Information
              </h3>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-3">
                {/* <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-600">Booking ID:</span>
                  <span className="text-[#0C1B3A] font-semibold">
                    {booking.id}
                  </span>
                </div> */}
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-600">
                    Reference Number:
                  </span>
                  <span className="text-[#0C1B3A] font-semibold">
                    {booking.refno}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-600">Type:</span>
                  <span className="text-[#0C1B3A] font-semibold">
                    {getTypeLabel(booking.type)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-600">Status:</span>
                  {getBookingStatusLabel(booking.bookingstatus)}
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-600">
                    Payment Status:
                  </span>
                  {getPaymentStatusLabel(booking.paymentstatus)}
                </div>
                 <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-600">Note:</span>
                  <span className="text-[#0C1B3A] font-semibold">
                    {booking.note??"-"}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Customer & Amenity Information */}
          <Card className="shadow-sm border-0">
            <CardHeader className="bg-[#0C1B3A] text-white">
              <h3 className="text-lg font-semibold flex items-center">
                <Icon icon="heroicons:user" className="mr-2" />
                Customer & Amenity
              </h3>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-600">Customer:</span>
                  <span className="text-[#0C1B3A] font-semibold">
                    {booking.playerName || "Not specified"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-600">Amenity:</span>
                  <span className="text-[#0C1B3A] font-semibold">
                    {booking.clubamenityName +
                      " (" +
                      booking.amenityName +
                      ")" || "Not specified"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-600">
                    Number of Persons:
                  </span>
                  <span className="text-[#0C1B3A] font-semibold">
                    {booking.noofpersons}
                  </span>
                </div>
                {booking.type === AmenityTypeEnum.Session_Based && (
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-600">Duration:</span>
                    <span className="text-[#0C1B3A] font-semibold">
                      {booking.duration}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Timing Details */}
          <Card className="shadow-sm border-0">
            <CardHeader className="bg-[#0C1B3A] text-white">
              <h3 className="text-lg font-semibold flex items-center">
                <Icon icon="heroicons:clock" className="mr-2" />
                Timing Details
              </h3>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-3">
                {booking.type === AmenityTypeEnum.Session_Based ? (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-600">
                        Booking Date:
                      </span>
                      <span className="text-[#0C1B3A] font-semibold">
                        {booking.bookingdate
                          ? moment(booking.bookingdate).format("DD MMM YYYY")
                          : "Not specified"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-600">
                        Start Time:
                      </span>
                      <span className="text-[#0C1B3A] font-semibold">
                        {booking.startdatetime
                          ? moment(booking.startdatetime).format(
                              "DD MMM YYYY, hh:mm A"
                            )
                          : "Not specified"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-600">
                        End Time:
                      </span>
                      <span className="text-[#0C1B3A] font-semibold">
                        {booking.enddatetime
                          ? moment(booking.enddatetime).format(
                              "DD MMM YYYY, hh:mm A"
                            )
                          : "Not specified"}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-600">
                        Daily Time:
                      </span>
                      <span className="text-[#0C1B3A] font-semibold">
                        {booking.starttime} - {booking.endtime}
                      </span>
                    </div>
                    <div className="flex justify-between items-start">
                      <span className="font-medium text-gray-600">
                        Selected Days:
                      </span>
                      <span className="text-[#0C1B3A] font-semibold text-right">
                        {getDaysLabel(booking.selecteddays)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Payment Details */}
          <Card className="shadow-sm border-0">
            <CardHeader className="bg-[#0C1B3A] text-white">
              <h3 className="text-lg font-semibold flex items-center">
                <Icon icon="heroicons:currency-dollar" className="mr-2" />
                Payment Details
              </h3>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-600">Charges:</span>
                  <span className="text-[#0C1B3A] font-semibold">
                    {booking.charges ? booking.charges.toFixed(2) : "0.00"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-600">Discount:</span>
                  <span className="text-[#0C1B3A] font-semibold">
                    {booking.discountamount
                      ? booking.discountamount.toFixed(2)
                      : "0.00"}
                  </span>
                </div>
                <div className="flex justify-between items-center border-t pt-2">
                  <span className="font-medium text-gray-600">Total:</span>
                  <span className="text-[#0C1B3A] font-bold text-lg">
                    {booking.total ? booking.total.toFixed(2) : "0.00"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-600">
                    Received Amount:
                  </span>
                  <span className="text-green-600 font-semibold">
                    {booking.receivedamount
                      ? booking.receivedamount.toFixed(2)
                      : "0.00"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-600">
                    Remaining Amount:
                  </span>
                  <span className="text-red-600 font-semibold">
                    {booking.remainingamount
                      ? booking.remainingamount.toFixed(2)
                      : "0.00"}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Booking Details */}
        {booking.bookingdetail && (
          <Card className="shadow-sm border-0 mt-6">
            <CardHeader className="bg-[#0C1B3A] text-white">
              <h3 className="text-lg font-semibold flex items-center">
                <Icon icon="heroicons:document-text" className="mr-2" />
                Booking Details
              </h3>
            </CardHeader>
            <CardContent className="p-4">
              <p className="text-gray-700 whitespace-pre-wrap">
                {booking.bookingdetail}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AmenityBookingDetails;
